import React, { useMemo, useRef, useState } from 'react'
import {
    Box,
    Flex,
    Icon,
    Image,
    Input,
    InputGroup,
    InputLeftElement,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Tag,
    TagLabel,
    Text,
    useOutsideClick,
} from '@chakra-ui/react'
import { ChevronDownIcon } from '@chakra-ui/icons'
import { FiCheck, FiSearch } from 'react-icons/fi'
import { FaCircleInfo, FaHospital } from 'react-icons/fa6'

import Logo from '~images/logo/logo.svg'
import { formatClinicName, stringToColor } from '~utils/helpers'
import ResponsiveClinicChips from './responsive-clinic-chips'
import { SwitchableClinic, useClinicSwitcher } from './use-clinic-switcher'
import IconWithTooltip from '~components/shared/icons/icon-with-tooltip'

export default function ClinicSwitchToolbar() {
    const {
        show,
        groups,
        selectedGroupId,
        setSelectedGroupId,
        clinics,
        currentClinicId,
        switchToClinic,
    } = useClinicSwitcher()

    const [query, setQuery] = useState('')
    const [isSearchOpen, setSearchOpen] = useState(false)
    const searchRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    useOutsideClick({
        ref: searchRef,
        handler: () => setSearchOpen(false),
    })

    const filteredClinics = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return clinics
        return clinics.filter(
            c => formatClinicName(c.name)?.toLowerCase().includes(q) ?? false,
        )
    }, [clinics, query])

    if (!show) return null

    const selectedGroup = groups.find(g => g.id === selectedGroupId)

    const handleSelect = (clinic: SwitchableClinic) => {
        switchToClinic(clinic)
        setSearchOpen(false)
        setQuery('')
    }

    const openSearch = () => {
        setSearchOpen(true)
        // Defer focus until the dropdown has mounted.
        requestAnimationFrame(() => inputRef.current?.focus())
    }

    return (
        <Flex
            display={{ base: 'none', lg: 'flex' }}
            align="center"
            gap={3}
            px={4}
            py={2}
            mx={{ lg: 2 }}
            mt={{ lg: 2 }}
            rounded={{ lg: 'xl' }}
            borderWidth={1}
            borderColor="gray.200"
            bg="white"
            minW={0}
        >
            <Flex align="center" gap={2} flexShrink={0} color="gray.500">
                <Tag colorScheme={stringToColor(selectedGroup?.name || '')}>
                    <TagLabel>{selectedGroup?.name}</TagLabel>
                </Tag>
                <IconWithTooltip
                    icon={FaCircleInfo}
                    label="Swap between clinics in this group"
                    fontSize="sm"
                    color="gray.600"
                />
            </Flex>

            {groups.length > 1 && (
                <Menu placement="bottom-start">
                    <MenuButton
                        as={Flex}
                        align="center"
                        gap={1}
                        px={2}
                        h="30px"
                        rounded="md"
                        borderWidth={1}
                        borderColor="gray.200"
                        bg="white"
                        cursor="pointer"
                        flexShrink={0}
                        _hover={{ bg: 'gray.50' }}
                    >
                        <Text
                            fontSize="sm"
                            fontWeight="medium"
                            noOfLines={1}
                            maxW="180px"
                        >
                            {selectedGroup?.name || 'Select group'}
                        </Text>
                        <ChevronDownIcon color="gray.500" />
                    </MenuButton>
                    <MenuList zIndex={6} maxH="320px" overflowY="auto">
                        {groups.map(group => (
                            <MenuItem
                                key={group.id}
                                onClick={() => setSelectedGroupId(group.id)}
                            >
                                <Flex
                                    align="center"
                                    justify="space-between"
                                    w="full"
                                    gap={2}
                                >
                                    <Text fontSize="sm" noOfLines={1}>
                                        {group.name}
                                    </Text>
                                    {group.id === selectedGroupId && (
                                        <Icon
                                            as={FiCheck}
                                            color="brand.500"
                                            boxSize={4}
                                        />
                                    )}
                                </Flex>
                            </MenuItem>
                        ))}
                    </MenuList>
                </Menu>
            )}

            <Box ref={searchRef} position="relative" w="240px" flexShrink={0}>
                <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                        <Icon as={FiSearch} color="gray.500" />
                    </InputLeftElement>
                    <Input
                        ref={inputRef}
                        rounded="md"
                        placeholder="Search clinics..."
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        onFocus={() => setSearchOpen(true)}
                    />
                </InputGroup>
                {isSearchOpen && (
                    <Box
                        position="absolute"
                        top="calc(100% + 4px)"
                        left={0}
                        right={0}
                        zIndex={20}
                        bg="white"
                        borderWidth={1}
                        borderColor="gray.200"
                        rounded="md"
                        shadow="lg"
                        maxH="320px"
                        overflowY="auto"
                    >
                        {filteredClinics.length === 0 ? (
                            <Text fontSize="sm" color="gray.500" px={3} py={3}>
                                No clinics found
                            </Text>
                        ) : (
                            filteredClinics.map(clinic => (
                                <Flex
                                    key={clinic.id}
                                    as="button"
                                    type="button"
                                    w="full"
                                    textAlign="left"
                                    align="center"
                                    gap={2}
                                    px={3}
                                    py={2}
                                    borderBottomWidth={1}
                                    borderColor="gray.100"
                                    _last={{ borderBottomWidth: 0 }}
                                    bg={
                                        clinic.id === currentClinicId
                                            ? 'brand.50'
                                            : 'white'
                                    }
                                    _hover={{ bg: 'gray.50' }}
                                    onClick={() => handleSelect(clinic)}
                                >
                                    <Image
                                        src={clinic.avatar_url || undefined}
                                        fallbackSrc={Logo}
                                        boxSize="20px"
                                        objectFit="contain"
                                        rounded="sm"
                                        flexShrink={0}
                                    />
                                    <Text
                                        flex={1}
                                        minW={0}
                                        fontSize="sm"
                                        noOfLines={1}
                                        color="gray.800"
                                    >
                                        {formatClinicName(clinic.name)}
                                    </Text>
                                    {clinic.id === currentClinicId && (
                                        <Icon
                                            as={FiCheck}
                                            color="brand.500"
                                            boxSize={4}
                                            flexShrink={0}
                                        />
                                    )}
                                </Flex>
                            ))
                        )}
                    </Box>
                )}
            </Box>

            <ResponsiveClinicChips
                clinics={clinics}
                currentClinicId={currentClinicId}
                onSelect={handleSelect}
                onOverflowClick={openSearch}
            />
        </Flex>
    )
}
